import { delay, put, select, takeLatest, all, call } from 'redux-saga/effects';
import { formatUnits, Provider, Signer } from 'ethers';
import { Errors } from '@blink/components/src/constants/errors';
import Logger from '@blink/components/src/utils/logger';
import { AbstractWallet } from '@blink/components/src/utils';

import { getWalletInfoSaga } from './common';

import { GET_USERS_SUPPLIES } from '@/shared/store/supplies/constants';
import { setUsersSupplies } from '@/shared/store/supplies/actions';
import { Store } from '@/shared/store';
import {
    clearErrorsAction,
    usersSuppliesErrorAction,
    userSuppliesBalanceErrorAction,
    userSuppliesRewardsErrorAction,
} from '@/shared/store/errors/actions';
import { UserSupply } from '@/shared/store/supplies';
import AgreementABI from '@/shared/abi/AgreementABI.json';
import { Agreement, ROLE } from '@/shared/api/borrow';
import { getSafeContract } from '@/shared/utils/getSafeContract';
import {
    USERS_SUPPLIES_BALANCE_ERROR,
    USERS_SUPPLIES_ERROR,
    USERS_SUPPLIES_PENDING_REWARDS_ERROR,
} from '@/shared/store/errors/constants';
import { getWalletMap } from '@/shared/utils/getWalletMap';

const PULL_INTERVAL = 10000;

/**
 *  Clear errors
 */
function* clearUserSuppliesErrors() {
    yield all([
        put(clearErrorsAction({ errorType: USERS_SUPPLIES_ERROR })),
        put(clearErrorsAction({ errorType: USERS_SUPPLIES_BALANCE_ERROR })),
        put(clearErrorsAction({ errorType: USERS_SUPPLIES_PENDING_REWARDS_ERROR })),
    ]);
}

/**
 * Get user balance
 */
function* fetchUserSuppliesBalance(
    agreements: Agreement[],
    walletAddress: string,
    signer: Signer | Provider,
): Generator<any, UserSupply[], any> {
    const userSupplies: UserSupply[] = yield all(
        agreements
            .filter((agr) => agr.address && agr.role === ROLE.LENDER)
            .map((agr) =>
                call(function* (): Generator<any, UserSupply | null, any> {
                    try {
                        const agreement = getSafeContract(agr.address, AgreementABI, signer);
                        const balance: bigint = yield agreement.balanceOf(walletAddress);

                        if (balance !== 0n) {
                            return {
                                agreementAddress: agr.address,
                                agreementTitle: agr.title,
                                totalDeposited: agr.totalDeposited,
                                totalDepositThreshold: agr.totalDepositThreshold,
                                balance: formatUnits(balance, agr.leverage.decimals),
                                reward: 'N/A',
                                apy: agr.apy,
                                tokenAddress: agr.leverage.address,
                                tokenDecimals: agr.leverage.decimals,
                                tokenSymbol: agr.leverage.symbol,
                            };
                        }
                    } catch (error) {
                        Logger.error('Error in supplies:agreement:balanceOf', { error });

                        yield put(
                            userSuppliesBalanceErrorAction({
                                message: Errors.USERS_SUPPLIES_BALANCE_ERROR + ' ' + agr.title,
                            }),
                        );
                    }
                    return null;
                }),
            ),
    );

    return userSupplies.filter((supply): supply is UserSupply => supply !== null);
}

/**
 * Rewards refresh
 */
function* fetchUserPendingRewardsLoop(
    agreements: Agreement[],
    walletAddress: string,
    signer: Signer | Provider,
    userSupplies: UserSupply[],
) {
    while (userSupplies.length) {
        yield all(
            agreements
                .filter((agr) => agr.address && agr.role === ROLE.LENDER)
                .map((agr) =>
                    call(function* () {
                        try {
                            const { type } = yield select((state: Store) => ({
                                type: state.wallets.active.type,
                            }));

                            const walletMap = getWalletMap();

                            const wallet: AbstractWallet = walletMap.get(type) as AbstractWallet;

                            if (!wallet) {
                                return;
                            }

                            const agreement = getSafeContract(agr.address, AgreementABI, signer);
                            const reward = yield agreement.pendingRewards(walletAddress);
                            const interestAccrued = formatUnits(
                                reward as any,
                                agr.leverage.decimals,
                            );
                            agr.interestAccrued = interestAccrued;

                            const supply = userSupplies.find(
                                (supply) => supply.agreementAddress === agr.address,
                            );
                            if (supply) {
                                supply.reward = interestAccrued;
                            }
                        } catch (error) {
                            Logger.error('Error in supplies:agreement:pendingRewards', { error });

                            yield put(
                                userSuppliesRewardsErrorAction({
                                    message:
                                        Errors.USERS_SUPPLIES_PENDING_REWARDS_ERROR +
                                        ' ' +
                                        agr.title,
                                }),
                            );
                        }
                    }),
                ),
        );

        yield put(setUsersSupplies([...userSupplies]));

        yield delay(PULL_INTERVAL);
    }
}

/**
 * Main saga
 */
export function* getUserSuppliesSaga(): Generator<any, any, any> {
    try {
        yield call(clearUserSuppliesErrors);

        const { agreements } = yield select((state: Store) => ({
            agreements: state.agreements.lenderAgreements,
        }));

        const walletData = yield call(getWalletInfoSaga, () => setUsersSupplies([]));
        if (!walletData) return;
        const { walletAddress, signer } = walletData;

        const userSupplies = yield call(
            fetchUserSuppliesBalance,
            agreements,
            walletAddress,
            signer,
        );
        yield put(setUsersSupplies(userSupplies));

        yield call(fetchUserPendingRewardsLoop, agreements, walletAddress, signer, userSupplies);
    } catch (e: any) {
        yield put(setUsersSupplies([]));
        yield put(
            usersSuppliesErrorAction({
                message:
                    e?.status === 403 || e?.status === 431
                        ? Errors.PERMISSION_DENIED + ' to get user supplies'
                        : Errors.USERS_SUPPLIES + ' ' + e.message,
            }),
        );
        Logger.error('Error in agreements::getUserSuppliesSaga:', { error: e });
    }
}

export function* suppliesWatcher() {
    yield takeLatest(GET_USERS_SUPPLIES, getUserSuppliesSaga);
}
